import useAgentInterception from '../../../hooks/agentInterception/useAgentInterception.hooks';
import { AgentInterceptionType } from '../../../reducers/messagingSlice';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { useAppDispatch } from '../../../app/hooks';
import { updateItemSlashCommandIdx } from '../../../reducers/menuSlice';
import { sendSpokenPrompt } from '../../../reducers/spokenPromptThunk';
import { TopLevelMenuItem } from '../../../utils/menu';
import { shouldSkipDonationPrompt } from '../../../utils/TTSMessage';
import { selectOrderValues } from '../../../selectors/order';
import { SLASH_COMMAND_REMINDERS } from '../../../utils/constants';

const useAutocompleteMessage = () => {
  const dispatch = useAppDispatch();
  const [currentMessage, setCurrentMessage] = useState('');
  const { sendAgentInterception } = useAgentInterception();
  const { total } = useSelector(selectOrderValues);
  const updateCurrentMessage = (message: string) => {
    setCurrentMessage(message);
  };
  const sendAgentInterventionEvent = (
    type: AgentInterceptionType,
    description?: string
  ) => {
    sendAgentInterception({
      data: {
        type,
        description,
      },
    });
  };
  /**
   * HITL-1587: placeholder substitution, the no-total skip, and the
   * spoken-total bookkeeping all live in the shared sendSpokenPrompt thunk —
   * the same path the cart-add auto-prompt and the center-pane heading use.
   * Only the interception (whose side effects are surface-specific) is
   * stamped here, and only when something was actually spoken. Returns
   * whether the prompt went out.
   */
  const sendMessage = (
    message: string,
    metadata?: any,
    title?: string
  ): boolean => {
    const { spoken } = dispatch(sendSpokenPrompt({ message, metadata }));
    if (spoken) {
      sendAgentInterventionEvent(AgentInterceptionType.TTS_SENT);
    }
    return spoken;
  };

  /**
   * HITL-1570: send the donation upsell prompt, gated on the constraint's
   * `speak_total_in_upsell` condition — with the condition set and no check
   * total yet, the donation upsell is skipped entirely (nothing is sent, and
   * false is returned so the caller skips its side effects too). The `{total}`
   * substitution and the spoken-total bookkeeping live in the shared
   * sendSpokenPrompt thunk that sendMessage routes through (HITL-1587); its
   * own no-total gate also covers a `{total}`-templated prompt whose
   * constraint is missing the condition. Returns whether the prompt was sent.
   */
  const sendDonationPromptMessage = (
    command: string,
    speakTotalInUpsell: boolean | undefined,
    metadata?: any
  ): boolean => {
    if (shouldSkipDonationPrompt({ speakTotalInUpsell, total })) {
      return false;
    }
    return sendMessage(command, metadata);
  };

  /**
   * HITL-1570: should a direct selection of this item be skipped outright?
   * True only for a donation item whose constraint declares
   * speak_total_in_upsell while no check total exists — the same gate the
   * reminder queue applies. Callers must then neither speak nor add the item.
   */
  const isDonationPromptSkipped = (item: {
    name?: string;
    speakTotalInUpsell?: boolean;
  }): boolean => {
    if ((item.name ?? '').split(':')[0] !== SLASH_COMMAND_REMINDERS.DONATION) {
      return false;
    }
    return shouldSkipDonationPrompt({
      speakTotalInUpsell: item.speakTotalInUpsell,
      total,
    });
  };

  /**
   * Returns whether a prompt was actually sent — false only when a gated
   * donation was skipped for lack of a check total, so the caller must skip
   * its own side effects (like adding the item to the cart) too.
   */
  const sendSlashCommandMessage = (
    item: TopLevelMenuItem,
    isUnifiedMenuEnabled: boolean
  ): boolean => {
    const itemName = item.name;
    let intent = '';
    let sendVal = '';
    if (isUnifiedMenuEnabled) {
      intent = itemName.replace('/', '');
      const { slashCommands, slashCommandIdx } = item;
      if (slashCommands && slashCommandIdx !== undefined) {
        sendVal = slashCommands[slashCommandIdx];
        const newIndex =
          slashCommandIdx === slashCommands.length - 1
            ? 0
            : slashCommandIdx + 1;
        dispatch(updateItemSlashCommandIdx({ itemName, newIndex }));
      }
    } else {
      [intent, sendVal] = itemName.replace('/', '').split(':');
    }
    // HITL-1570: the donation prompt is gated on the check total when its
    // constraint declares speak_total_in_upsell, and tracks whether the
    // total was spoken.
    if (itemName.split(':')[0] === SLASH_COMMAND_REMINDERS.DONATION) {
      return sendDonationPromptMessage(sendVal, item.speakTotalInUpsell, {
        intent,
      });
    }
    return sendMessage(sendVal, { intent });
  };

  return {
    currentMessage,
    updateCurrentMessage,
    sendMessage,
    sendDonationPromptMessage,
    isDonationPromptSkipped,
    sendAgentInterventionEvent,
    sendSlashCommandMessage,
  };
};

export default useAutocompleteMessage;
